import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Paper } from "@mui/material";
import { getNextMonthFirstDateFormatted } from "../../utils/dateUtils";

const SUMMARY_LINES = 3;
const SEE_MORE_LABEL = "See more";

const copyTextStyles = (from, to) => {
  const styles = getComputedStyle(from);
  to.style.font = styles.font;
  to.style.fontSize = styles.fontSize;
  to.style.fontFamily = styles.fontFamily;
  to.style.fontWeight = styles.fontWeight;
  to.style.lineHeight = styles.lineHeight;
  to.style.letterSpacing = styles.letterSpacing;
  to.style.whiteSpace = styles.whiteSpace;
  to.style.wordBreak = styles.wordBreak;
  to.style.textAlign = styles.textAlign;
};

const measureFits = (probe, text, buttonLabel, maxHeight) => {
  probe.replaceChildren();
  probe.append(document.createTextNode(`${text} `));
  const button = document.createElement("span");
  button.textContent = buttonLabel;
  button.style.fontWeight = "500";
  button.style.display = "inline-block";
  button.style.whiteSpace = "nowrap";
  probe.append(button);
  return probe.scrollHeight <= maxHeight;
};

const truncateForSeeMore = (probe, summary, maxHeight) => {
  let low = 0;
  let high = summary.length;
  let best = 0;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const candidate = summary.slice(0, mid).trimEnd();
    if (
      candidate &&
      measureFits(probe, `${candidate}…`, SEE_MORE_LABEL, maxHeight)
    ) {
      best = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  let cut = summary.slice(0, best).trimEnd();
  const lastBreak = Math.max(cut.lastIndexOf(" "), cut.lastIndexOf("\n"));
  if (lastBreak > cut.length * 0.5) {
    cut = cut.slice(0, lastBreak).trimEnd();
  }
  return cut ? `${cut}…` : summary;
};

const UsageSnapshotTile = ({ aiSummaryData = {} }) => {
  const summary = aiSummaryData?.summary;
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const [collapsedText, setCollapsedText] = useState(summary || "");
  const summaryRef = useRef(null);
  const seeMoreRef = useRef(null);
  const userToggledRef = useRef(false);

  useEffect(() => {
    setExpanded(false);
  }, [summary]);

  const handleToggle = () => {
    userToggledRef.current = true;
    setExpanded((open) => !open);
  };

  useLayoutEffect(() => {
    if (!userToggledRef.current) return;
    userToggledRef.current = false;
    if (expanded) {
      // Leave focus on the summary so the next Tab lands on See less,
      // instead of skipping to Professional Standards.
      summaryRef.current?.focus();
      return;
    }
    seeMoreRef.current?.focus();
  }, [expanded]);

  useEffect(() => {
    const el = summaryRef.current;
    if (!el || !summary) {
      setCanExpand(false);
      setCollapsedText(summary || "");
      return undefined;
    }

    const measure = () => {
      const width = el.clientWidth;
      if (!width) return;

      const styles = getComputedStyle(el);
      const lineHeight = parseFloat(styles.lineHeight) || 18;
      const maxHeight = lineHeight * SUMMARY_LINES + 1;
      const probe = document.createElement("div");
      probe.setAttribute("aria-hidden", "true");
      probe.style.cssText =
        "position:absolute;left:-9999px;top:0;visibility:hidden;pointer-events:none;";
      probe.style.width = `${width}px`;
      copyTextStyles(el, probe);
      document.body.appendChild(probe);

      try {
        probe.textContent = summary;
        if (probe.scrollHeight <= maxHeight) {
          setCanExpand(false);
          setCollapsedText(summary);
          return;
        }

        setCanExpand(true);
        setCollapsedText(truncateForSeeMore(probe, summary, maxHeight));
      } finally {
        probe.remove();
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [summary]);

  return (
    <Paper className="edsimsV2-sim-library-card" elevation={0}>
      <Box className="edsimsV2-usage-snapshot-header">
        <Box component="h2" className="edsimsV2-usage-snapshot-title">
          Participant Professional Learning Impact
        </Box>
        {summary ? (
          <>
            <div
              id="edsimsV2-usage-snapshot-summary"
              ref={summaryRef}
              tabIndex={-1}
              className="edsimsV2-usage-snapshot-body edsimsV2-usage-snapshot-summary"
            >
              {expanded ? summary : collapsedText}
              {canExpand && !expanded ? (
                <>
                  {" "}
                  <button
                    ref={seeMoreRef}
                    type="button"
                    className="edsimsV2-usage-snapshot-more"
                    onClick={handleToggle}
                    aria-expanded="false"
                    aria-controls="edsimsV2-usage-snapshot-summary"
                  >
                    {SEE_MORE_LABEL}
                  </button>
                </>
              ) : null}
              {canExpand && expanded ? (
                <>
                  {" "}
                  <button
                    type="button"
                    className="edsimsV2-usage-snapshot-more"
                    onClick={handleToggle}
                    aria-expanded="true"
                    aria-controls="edsimsV2-usage-snapshot-summary"
                  >
                    See less
                  </button>
                </>
              ) : null}
            </div>
            <p className="edsimsV2-usage-snapshot-meta">
              <span className="edsimsV2-usage-snapshot-meta-label">
                Last Update:
              </span>{" "}
              <span className="edsimsV2-usage-snapshot-meta-date">
                {aiSummaryData?.lastUpdated}
              </span>
            </p>
            <p className="edsimsV2-usage-snapshot-meta">
              <span className="edsimsV2-usage-snapshot-meta-label">
                Next Update:
              </span>{" "}
              <span className="edsimsV2-usage-snapshot-meta-date">
                {aiSummaryData?.nextUpdateDue}
              </span>
            </p>
          </>
        ) : (
          <p className="edsimsV2-usage-snapshot-body edsimsV2-usage-snapshot-body--empty">
            The next updated summary will be available on{" "}
            {getNextMonthFirstDateFormatted()}.
          </p>
        )}
      </Box>
    </Paper>
  );
};

export default UsageSnapshotTile;
